import { Link } from "@tanstack/react-router";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import type { OrgHealthInsight } from "@/hooks/use-dashboard-insights";
import { cn } from "@/lib/utils";
import { HomeCard, HomeCardHead } from "./home-card";

/**
 * A single labelled stat tile. `tone` optionally colours the number. When `to`
 * is given the tile becomes a link that drills into the records behind the
 * count (e.g. the Inactive tile → the People list filtered to inactive).
 */
function StatTile({
  n,
  label,
  tone,
  to,
  search,
}: {
  n: number;
  label: string;
  tone?: string;
  to?: string;
  search?: Record<string, string>;
}) {
  const content = (
    <>
      <span
        className={cn("font-display text-2xl font-bold leading-none text-card-foreground", tone)}
      >
        {n}
      </span>
      <span className="mt-1.5 text-2xs font-medium leading-tight text-muted-foreground">
        {label}
      </span>
    </>
  );
  const base = "flex flex-col rounded-[10px] border bg-card px-3.5 py-3 text-left";

  if (!to) {
    return <div className={base}>{content}</div>;
  }
  return (
    <Link
      to={to}
      search={search}
      className={cn(
        base,
        "transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-0.5 hover:border-input hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      {content}
    </Link>
  );
}

/**
 * Admin-only org-health snapshot: headcount by status and employment type, plus
 * two "needs attention" counts (unfilled roles, not-started certifications).
 */
export function OrgHealthTiles({
  orgHealth,
  isLoading,
  isError,
  onRetry,
}: {
  orgHealth: OrgHealthInsight | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  return (
    <HomeCard>
      <HomeCardHead title="At a glance" />
      <div className="px-5 py-3.5">
        {isLoading ? (
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:grid-cols-8">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <Skeleton key={i} className="h-16 rounded-[10px]" />
            ))}
          </div>
        ) : isError ? (
          <ErrorState
            className="py-4"
            message="Couldn't load organization health"
            onRetry={onRetry}
          />
        ) : orgHealth ? (
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:grid-cols-8">
            {/* Headcount tiles drill into the People list. Employment-type tiles
                count across ALL statuses, so they clear the list's active-only
                default (statuses=active,inactive) to keep the count matching.
                fromPage=org-health makes the destination list breadcrumb back
                here rather than to its own catalog root. */}
            <StatTile
              n={orgHealth.headcountByStatus.active}
              label="Active"
              to="/dashboard/persons"
              search={{ statuses: "active", fromPage: "org-health" }}
            />
            <StatTile
              n={orgHealth.headcountByStatus.inactive}
              label="Inactive"
              to="/dashboard/persons"
              search={{ statuses: "inactive", fromPage: "org-health" }}
            />
            <StatTile
              n={orgHealth.headcountByEmploymentType.fullTime}
              label="Full-time"
              to="/dashboard/persons"
              search={{
                statuses: "active,inactive",
                employmentTypes: "full_time",
                fromPage: "org-health",
              }}
            />
            <StatTile
              n={orgHealth.headcountByEmploymentType.partTime}
              label="Part-time"
              to="/dashboard/persons"
              search={{
                statuses: "active,inactive",
                employmentTypes: "part_time",
                fromPage: "org-health",
              }}
            />
            <StatTile
              n={orgHealth.headcountByEmploymentType.contractor}
              label="Contractors"
              to="/dashboard/persons"
              search={{
                statuses: "active,inactive",
                employmentTypes: "contractor",
                fromPage: "org-health",
              }}
            />
            <StatTile
              n={orgHealth.rolesWithNoPeople}
              label="Unfilled roles"
              tone={orgHealth.rolesWithNoPeople > 0 ? "text-warning-foreground" : undefined}
              to="/dashboard/roles"
              search={{ vacant: "only", fromPage: "org-health" }}
            />
            <StatTile
              n={orgHealth.assignedNotStarted}
              label="Certs not started"
              tone={orgHealth.assignedNotStarted > 0 ? "text-warning-foreground" : undefined}
              to="/dashboard/certifications/not-started"
            />
            {/* Data-quality: active people whose manager is inactive (WI-076).
                Drills into the directory pre-filtered to exactly those people
                so the reporting lines can be corrected. */}
            <StatTile
              n={orgHealth.inactiveManagerCount}
              label="Inactive manager"
              tone={orgHealth.inactiveManagerCount > 0 ? "text-warning-foreground" : undefined}
              to="/dashboard/persons"
              search={{ managerInactive: "only", fromPage: "org-health" }}
            />
          </div>
        ) : (
          <p className="py-4 text-center text-xs text-muted-foreground">
            No organization data yet.
          </p>
        )}
      </div>
    </HomeCard>
  );
}
